class ProductFreeGifts extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.section;
    this.gift1Enabled = this.dataset.gift1Enabled === 'true';
    this.gift2Enabled = this.dataset.gift2Enabled === 'true';

    const mainVariantsScript = this.querySelector('[data-pfg-main-variants]');
    const gift1VariantsScript = this.querySelector('[data-pfg-gift1-variants]');
    this.mainVariants = mainVariantsScript ? this.safeParse(mainVariantsScript.textContent) : [];
    this.gift1Variants = gift1VariantsScript ? this.safeParse(gift1VariantsScript.textContent) : [];
    this.optionNames = this.dataset.optionNames ? this.safeParse(this.dataset.optionNames) : [];

    this.gift1Form = this.querySelector('[data-pfg-gift1-form]');
    this.gift2Form = this.querySelector('[data-pfg-gift2-form]');
    this.gift1IdInput = this.querySelector('[data-pfg-gift1-id-input]');

    this.currentGift1Variant = null;
    this.submitting = false;
    this.onVariantAreaChange = this.onVariantAreaChange.bind(this);
    this.onSubmitButtonClick = this.onSubmitButtonClick.bind(this);
  }

  log(...args) {
    console.info('[product-free-gifts]', ...args);
  }

  connectedCallback() {
    if (this.gift1Enabled) {
      this.updateGift1();
      // Delegated on document (not bound to a captured element reference):
      // the theme's own <variant-selects> custom element can be re-rendered
      // after a selection change. A listener attached to a specific node
      // captured once at connect-time goes stale the moment that happens.
      document.addEventListener('change', this.onVariantAreaChange);
      document.addEventListener('input', this.onVariantAreaChange);
    }
    // We fully own the Add to Cart click: call preventDefault() on the click
    // itself (not the later 'submit' event) so the button's default action —
    // actually submitting the form — never happens in the first place. That
    // reliably stops the plain, non-AJAX form POST that was causing a hard
    // navigation to /cart instead of opening the drawer: this theme's own JS
    // clearly isn't reliably intercepting this particular form (that's what a
    // real page navigation on submit means — no AJAX handler ran at all), so
    // we no longer depend on it doing so. We add all three items ourselves.
    const mainForm = this.mainForm();
    if (mainForm) {
      const submitButton = mainForm.querySelector('button[type="submit"]');
      if (submitButton) {
        submitButton.addEventListener('click', this.onSubmitButtonClick);
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onVariantAreaChange);
    document.removeEventListener('input', this.onVariantAreaChange);
    const mainForm = this.mainForm();
    if (mainForm) {
      const submitButton = mainForm.querySelector('button[type="submit"]');
      if (submitButton) {
        submitButton.removeEventListener('click', this.onSubmitButtonClick);
      }
    }
  }

  safeParse(text) {
    try {
      return JSON.parse(text);
    } catch (error) {
      return [];
    }
  }

  // All of the following re-query the live DOM on every call rather than
  // caching a node reference, since the theme's own JS can replace these
  // elements at any time after variant changes.
  mainFormWrapper() {
    return document.getElementById('ProductForm-' + this.sectionId);
  }

  mainForm() {
    const wrapper = this.mainFormWrapper();
    return wrapper ? wrapper.querySelector('form[data-type="add-to-cart-form"]') : null;
  }

  mainVariantInput() {
    const wrapper = this.mainFormWrapper();
    return wrapper ? wrapper.querySelector('.product-variant-id') : null;
  }

  variantSelectsRoot() {
    return (
      document.getElementById('variant-selects-' + this.sectionId) ||
      document.querySelector('variant-selects[data-section="' + this.sectionId + '"]')
    );
  }

  currentMainVariantId() {
    const input = this.mainVariantInput();
    return input ? input.value : null;
  }

  // Reads the checked radio / selected dropdown for every option directly from
  // the DOM. Native inputs update synchronously the instant the user clicks/
  // selects, before any 'change' listener runs. Matches both bare option
  // names (pills/swatches: name="Color") and bracketed dropdown names
  // (name="options[Color]").
  getSelectedOptionValues() {
    const root = this.variantSelectsRoot();
    if (!root || !this.optionNames.length) return null;
    const values = [];
    for (const name of this.optionNames) {
      const bracketed = 'options[' + name + ']';
      const matchesName = (el) => el.name === name || el.name === bracketed;

      const checked = Array.from(root.querySelectorAll('input[type="radio"], input[type="checkbox"]')).find(
        (el) => matchesName(el) && el.checked
      );
      if (checked) {
        values.push(checked.value);
        continue;
      }
      const select = Array.from(root.querySelectorAll('select')).find(matchesName);
      if (select) {
        values.push(select.value);
        continue;
      }
      return null;
    }
    return values;
  }

  findMainVariantByOptionValues(values) {
    if (!values) return null;
    return (
      this.mainVariants.find((variant) => {
        if (!Array.isArray(variant.options) || variant.options.length !== values.length) return false;
        return variant.options.every((value, index) => this.normalize(value) === this.normalize(values[index]));
      }) || null
    );
  }

  onVariantAreaChange(event) {
    const root = this.variantSelectsRoot();
    if (root && !root.contains(event.target)) return;
    if (!root && !this.mainFormWrapper()) return;
    this.updateGift1();
  }

  normalize(value) {
    return (value || '').toString().trim().toLowerCase();
  }

  matchGift1Variant(colorValue) {
    if (!this.gift1Variants.length) return null;
    const target = this.normalize(colorValue);
    let match = null;
    if (target) {
      match = this.gift1Variants.find((variant) => this.normalize(variant.color) === target && variant.available);
      if (!match) {
        match = this.gift1Variants.find((variant) => this.normalize(variant.color) === target);
      }
    }
    if (!match) {
      match = this.gift1Variants.find((variant) => variant.available) || this.gift1Variants[0];
    }
    return match || null;
  }

  updateGift1() {
    let mainVariant = this.findMainVariantByOptionValues(this.getSelectedOptionValues());
    if (!mainVariant) {
      const mainVariantId = this.currentMainVariantId();
      mainVariant = this.mainVariants.find((variant) => String(variant.id) === String(mainVariantId)) || null;
    }
    const color = mainVariant ? mainVariant.color : null;
    const match = this.matchGift1Variant(color);
    if (!match) return;

    this.currentGift1Variant = match;

    if (this.gift1IdInput) this.gift1IdInput.value = match.id;

    const colorLabel = this.querySelector('[data-pfg-gift1-color]');
    if (colorLabel) colorLabel.textContent = match.color || '';

    const image = this.querySelector('[data-pfg-gift1-image]');
    if (image && match.image) image.src = match.image;

    this.classList.toggle('pfg-gift1-unavailable', !match.available);
  }

  onSubmitButtonClick(event) {
    const mainForm = this.mainForm();
    if (!mainForm) {
      this.log('main form not found — letting the click proceed natively.');
      return;
    }

    // Prevent the click's default action outright. This is what stops the
    // browser from ever submitting the form as a plain POST (which is what a
    // hard navigation to /cart means happened before) — it does not depend on
    // any 'submit' event or on racing against the theme's own JS at all.
    event.preventDefault();

    if (this.submitting) {
      this.log('already submitting, ignoring extra click.');
      return;
    }

    if (typeof mainForm.reportValidity === 'function' && !mainForm.checkValidity()) {
      this.log('main form invalid — showing native validation UI instead of adding.');
      mainForm.reportValidity();
      return;
    }

    this.performAdd(mainForm);
  }

  performAdd(mainForm) {
    this.submitting = true;
    this.setButtonLoading(mainForm, true);
    this.clearFormError(mainForm);

    const groupId = 'gg-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
    this.setGroupProperty(mainForm, groupId);

    // Resolve the gift one more time right before adding, in case the color
    // changed since the last recorded update.
    this.updateGift1();

    const mainItem = this.buildItemFromForm(mainForm);
    this.log('adding main item:', mainItem);

    const quantity = mainItem.quantity;
    const giftItems = [];

    if (this.gift1Enabled) {
      if (!this.currentGift1Variant) {
        this.log('gift1 SKIPPED: no matched variant resolved');
      } else if (!this.currentGift1Variant.available) {
        this.log('gift1 SKIPPED: matched variant unavailable', this.currentGift1Variant);
      } else if (this.gift1Form) {
        const gift1FormEl = this.gift1Form.querySelector('form');
        if (gift1FormEl) {
          giftItems.push(
            this.buildItemFromForm(gift1FormEl, {
              id: this.currentGift1Variant.id,
              quantity,
              'properties[_gift_group]': groupId,
            })
          );
        }
      }
    }
    if (this.gift2Enabled) {
      if (!this.gift2Form) {
        this.log('gift2 SKIPPED: hidden form not found');
      } else if (this.gift2Form.dataset.available === 'false') {
        this.log('gift2 SKIPPED: unavailable (out of stock)');
      } else {
        const gift2FormEl = this.gift2Form.querySelector('form');
        if (gift2FormEl) {
          giftItems.push(
            this.buildItemFromForm(gift2FormEl, {
              quantity,
              'properties[_gift_group]': groupId,
            })
          );
        }
      }
    }

    this.log(
      'gift items to add:',
      giftItems.length,
      giftItems.map((i) => i.id)
    );

    // Main product first — a genuine failure here (out of stock, etc.) must
    // surface to the customer via the theme's existing error UI. Gifts are
    // added afterward, sequentially (one in-flight request at a time), and a
    // gift failing is swallowed — it must never block or undo the main add.
    this.addSingleItem(mainItem)
      .then(() => {
        this.log('main item added OK.');
        return giftItems.reduce(
          (promise, item) =>
            promise.then(() =>
              this.addSingleItem(item)
                .then(() => this.log('gift added OK:', item.id))
                .catch((error) => this.log('gift FAILED:', item.id, error.message))
            ),
          Promise.resolve()
        );
      })
      .then(() => this.refreshCartAndOpenDrawer())
      .catch((error) => {
        this.log('main item FAILED:', error.message);
        this.showFormError(mainForm, error.message);
      })
      .finally(() => {
        this.submitting = false;
        this.setButtonLoading(mainForm, false);
      });
  }

  setGroupProperty(form, groupId) {
    let input = form.querySelector('input[name="properties[_gift_group]"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[_gift_group]';
      form.appendChild(input);
    }
    input.value = groupId;
  }

  buildItemFromForm(form, overrides) {
    const formData = new FormData(form);
    if (overrides) {
      Object.keys(overrides).forEach((key) => formData.set(key, overrides[key]));
    }
    const item = { id: formData.get('id'), quantity: parseInt(formData.get('quantity'), 10) || 1, properties: {} };
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value !== '') item.properties[match[1]] = value;
    }
    if (!Object.keys(item.properties).length) delete item.properties;
    return item;
  }

  addSingleItem(item) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(item),
    })
      .then((response) => response.json())
      .then((result) => {
        if (result.status) {
          throw new Error(result.description || result.message || 'Could not add product to cart');
        }
        return result;
      });
  }

  setButtonLoading(mainForm, isLoading) {
    const button = mainForm.querySelector('button[type="submit"]');
    if (!button) return;
    button.disabled = isLoading;
    button.classList.toggle('loading', isLoading);
    const spinner = button.querySelector('.loading-overlay__spinner');
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
  }

  clearFormError(mainForm) {
    const wrapper = mainForm.querySelector('.product-form__error-message-wrapper');
    if (wrapper) wrapper.hidden = true;
  }

  showFormError(mainForm, message) {
    const wrapper = mainForm.querySelector('.product-form__error-message-wrapper');
    const errorText = wrapper ? wrapper.querySelector('.product-form__error-message') : null;
    if (wrapper && errorText) {
      errorText.textContent = message;
      wrapper.hidden = false;
    }
  }

  refreshCartAndOpenDrawer() {
    if (window.__pfgSyncBusy !== undefined) window.__pfgSyncBusy = true;

    const sectionIds = new Set();
    document.querySelectorAll('[id^="shopify-section-"]').forEach((sectionEl) => {
      if (
        sectionEl.querySelector('#cart-icon-bubble') ||
        sectionEl.querySelector('cart-drawer-items, [data-gift-scope]') ||
        sectionEl.querySelector('cart-drawer')
      ) {
        sectionIds.add(sectionEl.id.replace('shopify-section-', ''));
      }
    });

    const finishAndOpen = () => {
      if (window.__pfgSyncBusy !== undefined) window.__pfgSyncBusy = false;
      // Only trigger opening if the drawer isn't already open — the link the
      // theme wires up to open it may just toggle, so clicking it while
      // already open could close it instead.
      const drawer = document.querySelector('cart-drawer');
      const alreadyOpen = drawer && drawer.classList.contains('active');
      if (alreadyOpen) {
        this.log('drawer already open, leaving as-is.');
        return;
      }
      const cartLink = document.querySelector('a.header__icon--cart, a[href="' + this.cartUrl() + '"]');
      if (cartLink) {
        this.log('opening cart drawer.');
        cartLink.click();
      } else {
        this.log('could not find cart icon link to open the drawer.');
      }
    };

    if (!sectionIds.size) {
      this.log('no cart-drawer/cart-icon sections found on page to refresh.');
      finishAndOpen();
      return Promise.resolve();
    }

    const url = new URL(window.location.href);
    url.searchParams.set('sections', Array.from(sectionIds).join(','));

    return fetch(url.toString())
      .then((response) => (response.ok ? response.json() : null))
      .then((sections) => {
        if (sections) {
          Object.keys(sections).forEach((id) => {
            const target = document.getElementById('shopify-section-' + id);
            if (!target || !sections[id]) return;
            const wrapper = document.createElement('div');
            wrapper.innerHTML = sections[id];
            const newInner = wrapper.firstElementChild;
            if (newInner) target.innerHTML = newInner.innerHTML;
          });
        }
      })
      .catch((error) => this.log('section refresh failed:', error.message))
      .finally(finishAndOpen);
  }

  cartUrl() {
    return (
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') +
      'cart'
    );
  }
}

if (!customElements.get('product-free-gifts')) {
  customElements.define('product-free-gifts', ProductFreeGifts);
}
